import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

import { nodeEditorService } from '../../services/nodeEditorService';
import { NodeEditorSession, NodeEditorLLMConfig, NodeEditorPrompt } from '../../types/nodeEditor.types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, index, value }) => {
  return value === index ? <Box sx={{ p: 2 }}>{children}</Box> : null;
};

interface SettingsPanelProps {
  session: NodeEditorSession;
}

export const SettingsPanel: React.FC<SettingsPanelProps> = ({ session }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [llmConfigs, setLlmConfigs] = useState<NodeEditorLLMConfig[]>([]);
  const [prompts, setPrompts] = useState<NodeEditorPrompt[]>([]);
  const [permissions, setPermissions] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // LLM Config dialog
  const [showLLMDialog, setShowLLMDialog] = useState(false);
  const [editingLLM, setEditingLLM] = useState<NodeEditorLLMConfig | null>(null);
  const [llmForm, setLlmForm] = useState({
    name: '',
    provider: 'openai',
    api_key: '',
    api_base: '',
    model: 'gpt-4',
    temperature: 0.7,
    max_tokens: 4096,
    system_prompt: '',
  });
  
  // Prompt dialog
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<NodeEditorPrompt | null>(null);
  const [promptForm, setPromptForm] = useState({
    name: '',
    prompt_text: '',
    description: '',
    prompt_type: 'user',
    category: '',
    is_public: false,
  });
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const [configs, userPrompts, perms] = await Promise.all([
        nodeEditorService.getLLMConfigs(),
        nodeEditorService.getPrompts(),
        nodeEditorService.getMyPermissions(),
      ]);
      setLlmConfigs(configs);
      setPrompts(userPrompts);
      setPermissions(perms);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveLLMConfig = async () => {
    try {
      if (editingLLM) {
        await nodeEditorService.updateLLMConfig(editingLLM.id, llmForm);
      } else {
        await nodeEditorService.createLLMConfig(llmForm);
      }
      setShowLLMDialog(false);
      loadData();
      setMessage({ type: 'success', text: 'LLM configuration saved' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save LLM configuration' });
    }
  };
  
  const handleSavePrompt = async () => {
    try {
      if (editingPrompt) {
        await nodeEditorService.updatePrompt(editingPrompt.id, promptForm);
      } else {
        await nodeEditorService.createPrompt(promptForm);
      }
      setShowPromptDialog(false);
      loadData();
      setMessage({ type: 'success', text: 'Prompt saved' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save prompt' });
    }
  };
  
  const handleDeleteLLM = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this LLM configuration?')) return;
    try {
      await nodeEditorService.deleteLLMConfig(id);
      loadData();
      setMessage({ type: 'success', text: 'LLM configuration deleted' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete LLM configuration' });
    }
  };
  
  const handleDeletePrompt = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) return;
    try {
      await nodeEditorService.deletePrompt(id);
      loadData();
      setMessage({ type: 'success', text: 'Prompt deleted' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete prompt' });
    }
  };
  
  const openLLMDialog = (config?: NodeEditorLLMConfig) => {
    if (config) {
      setEditingLLM(config);
      setLlmForm({
        name: config.name,
        provider: config.provider,
        api_key: '',
        api_base: config.api_base,
        model: config.model,
        temperature: config.temperature,
        max_tokens: config.max_tokens,
        system_prompt: config.system_prompt,
      });
    } else {
      setEditingLLM(null);
      setLlmForm({
        name: '',
        provider: 'openai',
        api_key: '',
        api_base: '',
        model: 'gpt-4',
        temperature: 0.7,
        max_tokens: 4096,
        system_prompt: '',
      });
    }
    setShowLLMDialog(true);
  };
  
  const openPromptDialog = (prompt?: NodeEditorPrompt) => {
    if (prompt) {
      setEditingPrompt(prompt);
      setPromptForm({
        name: prompt.name,
        prompt_text: prompt.prompt_text,
        description: prompt.description,
        prompt_type: prompt.prompt_type,
        category: prompt.category,
        is_public: prompt.is_public,
      });
    } else {
      setEditingPrompt(null);
      setPromptForm({
        name: '',
        prompt_text: '',
        description: '',
        prompt_type: 'user',
        category: '',
        is_public: false,
      });
    }
    setShowPromptDialog(true);
  };
  
  const providers = [
    { value: 'openai', label: 'OpenAI', models: ['gpt-4', 'gpt-4-turbo', 'gpt-3.5-turbo'] },
    { value: 'anthropic', label: 'Anthropic Claude', models: ['claude-3-opus', 'claude-3-sonnet', 'claude-3-haiku'] },
    { value: 'google', label: 'Google Gemini', models: ['gemini-pro', 'gemini-pro-vision'] },
    { value: 'azure', label: 'Azure OpenAI', models: ['gpt-4', 'gpt-35-turbo'] },
    { value: 'ollama', label: 'Ollama (Local)', models: ['llama2', 'codellama', 'mistral'] },
  ];
  
  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      <Typography variant="h6" sx={{ p: 2 }}>Settings</Typography>
      
      {message && (
        <Alert 
          severity={message.type} 
          onClose={() => setMessage(null)}
          sx={{ mx: 2, mb: 2 }}
        >
          {message.text}
        </Alert>
      )}
      
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
        <Tab label="LLM Configuration" />
        <Tab label="Prompts" />
        <Tab label="Session Settings" />
        <Tab label="Permissions" />
      </Tabs>
      
      {/* LLM Configuration Tab */}
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1">LLM Configurations</Typography>
          {permissions?.can_manage_llm_configs && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openLLMDialog()}
            >
              Add Configuration
            </Button>
          )}
        </Box>
        
        <List>
          {llmConfigs.map((config) => (
            <ListItem key={config.id} divider>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {config.name}
                    {config.is_default && <Chip size="small" label="Default" color="primary" />}
                  </Box>
                }
                secondary={`${config.provider} - ${config.model}`}
              />
              <ListItemSecondaryAction>
                <IconButton onClick={() => openLLMDialog(config)}>
                  <EditIcon />
                </IconButton>
                {!config.is_default && (
                  <IconButton onClick={() => handleDeleteLLM(config.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </TabPanel>
      
      {/* Prompts Tab */}
      <TabPanel value={activeTab} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1">Saved Prompts</Typography>
          {permissions?.can_manage_prompts && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openPromptDialog()}
            >
              Add Prompt
            </Button>
          )}
        </Box>
        
        <List>
          {prompts.map((prompt) => (
            <ListItem key={prompt.id} divider>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {prompt.name}
                    {prompt.is_preset && <Chip size="small" label="Preset" color="secondary" />}
                    {prompt.is_system && <Chip size="small" label="System" color="error" />}
                  </Box>
                }
                secondary={prompt.description || prompt.prompt_text.substring(0, 50) + '...'}
              />
              <ListItemSecondaryAction>
                <IconButton onClick={() => openPromptDialog(prompt)}>
                  <EditIcon />
                </IconButton>
                {!prompt.is_preset && !prompt.is_system && (
                  <IconButton onClick={() => handleDeletePrompt(prompt.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </TabPanel>
      
      {/* Session Settings Tab */}
      <TabPanel value={activeTab} index={2}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Session Settings</Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={session.auto_save_enabled}
                onChange={(e) => {
                  // Update session auto-save
                }}
              />
            }
            label="Enable Auto-Save"
          />
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Auto-save interval: {session.auto_save_interval} seconds
            </Typography>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="body2" color="textSecondary">
            Created: {new Date(session.created_at).toLocaleString()}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Last saved: {new Date(session.last_saved).toLocaleString()}
          </Typography>
          {session.is_published && (
            <Typography variant="body2" color="success.main">
              Published: {session.published_url}
            </Typography>
          )}
        </Paper>
      </TabPanel>
      
      {/* Permissions Tab */}
      <TabPanel value={activeTab} index={3}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Your Permissions</Typography>
          
          {permissions && (
            <List dense>
              {Object.entries(permissions).map(([key, value]) => (
                <ListItem key={key}>
                  <ListItemText
                    primary={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  />
                  <Chip
                    size="small"
                    label={value ? 'Yes' : 'No'}
                    color={value ? 'success' : 'default'}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      </TabPanel>
      
      {/* LLM Config Dialog */}
      <Dialog open={showLLMDialog} onClose={() => setShowLLMDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingLLM ? 'Edit' : 'Add'} LLM Configuration</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={llmForm.name}
            onChange={(e) => setLlmForm({ ...llmForm, name: e.target.value })}
            margin="normal"
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Provider</InputLabel>
            <Select
              value={llmForm.provider}
              onChange={(e) => setLlmForm({ ...llmForm, provider: e.target.value })}
            >
              {providers.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            label="API Key"
            type="password"
            value={llmForm.api_key}
            onChange={(e) => setLlmForm({ ...llmForm, api_key: e.target.value })}
            margin="normal"
            helperText={editingLLM ? "Leave blank to keep existing key" : "Required"}
          />
          
          <TextField
            fullWidth
            label="API Base URL (optional)"
            value={llmForm.api_base}
            onChange={(e) => setLlmForm({ ...llmForm, api_base: e.target.value })}
            margin="normal"
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Model</InputLabel>
            <Select
              value={llmForm.model}
              onChange={(e) => setLlmForm({ ...llmForm, model: e.target.value })}
            >
              {providers.find(p => p.value === llmForm.provider)?.models.map((m) => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2">Temperature: {llmForm.temperature}</Typography>
            <Slider
              value={llmForm.temperature}
              onChange={(e, v) => setLlmForm({ ...llmForm, temperature: v as number })}
              min={0}
              max={2}
              step={0.1}
            />
          </Box>
          
          <TextField
            fullWidth
            label="Max Tokens"
            type="number"
            value={llmForm.max_tokens}
            onChange={(e) => setLlmForm({ ...llmForm, max_tokens: parseInt(e.target.value) })}
            margin="normal"
          />
          
          <TextField
            fullWidth
            label="System Prompt"
            value={llmForm.system_prompt}
            onChange={(e) => setLlmForm({ ...llmForm, system_prompt: e.target.value })}
            margin="normal"
            multiline
            rows={6}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLLMDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveLLMConfig} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Prompt Dialog */}
      <Dialog open={showPromptDialog} onClose={() => setShowPromptDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingPrompt ? 'Edit' : 'Add'} Prompt</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={promptForm.name}
            onChange={(e) => setPromptForm({ ...promptForm, name: e.target.value })}
            margin="normal"
          />
          
          <TextField
            fullWidth
            label="Description"
            value={promptForm.description}
            onChange={(e) => setPromptForm({ ...promptForm, description: e.target.value })}
            margin="normal"
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Type</InputLabel>
            <Select
              value={promptForm.prompt_type}
              onChange={(e) => setPromptForm({ ...promptForm, prompt_type: e.target.value })}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="quick">Quick Link</MenuItem>
              <MenuItem value="template">Template</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            label="Prompt Text"
            value={promptForm.prompt_text}
            onChange={(e) => setPromptForm({ ...promptForm, prompt_text: e.target.value })}
            margin="normal"
            multiline
            rows={8}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={promptForm.is_public}
                onChange={(e) => setPromptForm({ ...promptForm, is_public: e.target.checked })}
              />
            }
            label="Make public to organization"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPromptDialog(false)}>Cancel</Button>
          <Button onClick={handleSavePrompt} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPanel;
